import React, { useState, useEffect } from 'react';
import { ChevronLeft, Clock, BookOpen, Layers, ArrowLeft, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * GovExamView - Government exam selection view with chapter support
 * Supports practice by chapter and comprehensive exam
 */
function GovExamView({ 
  examType,
  exam,
  onBack,
  onStartQuiz,
  onStartSimulator,
  onStartChapterQuiz
}) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const examConfigs = {
    'medical': {
      title: 'מבחן הרישוי הממשלתי ברפואה',
      subtitle: '3,000 שאלות מתעדכנות לסילבוס 2026',
      icon: '⚕️',
      gradient: 'from-blue-600 to-indigo-700',
      examId: 'medical-gov',
      simulatorView: 'medical-simulator',
      simulatorTitle: 'סימולציה מלאה - 150 שאלות (210 דק\')',
      stats: { easy: 1012, medium: 994, hard: 994 },
      bilingual: true
    },
    'nursing': {
      title: 'מבחן הרישוי הממשלתי בסיעוד',
      subtitle: '3,000 שאלות מתעדכנות לסילבוס 2026',
      icon: '👩‍⚕️',
      gradient: 'from-teal-600 to-emerald-700',
      examId: 'nursing-gov',
      simulatorView: 'nursing-simulator',
      simulatorTitle: 'סימולציה מלאה - 150 שאלות (180 דק\')',
      stats: { easy: 1024, medium: 986, hard: 990 },
      bilingual: true
    },
    'dental': {
      title: 'מבחן הרישוי הממשלתי ברפואת שיניים',
      subtitle: '2,500 שאלות מתעדכנות לסילבוס 2026',
      icon: '🦷',
      gradient: 'from-cyan-600 to-blue-700',
      examId: 'dental-gov',
      simulatorView: 'dental-simulator',
      simulatorTitle: 'סימולציה מלאה - 150 שאלות (180 דק\')',
      stats: { easy: 850, medium: 825, hard: 825 },
      bilingual: true
    },
    'pharmacy': {
      title: 'מבחן הרישוי הממשלתי ברוקחות',
      subtitle: '2,000 שאלות מתעדכנות לסילבוס 2026',
      icon: '💊',
      gradient: 'from-purple-600 to-violet-700',
      examId: 'pharmacy-gov',
      simulatorView: 'pharmacy-simulator',
      simulatorTitle: 'סימולציה מלאה - 120 שאלות (150 דק\')',
      stats: { easy: 680, medium: 660, hard: 660 },
      bilingual: true
    },
    'bar-exam': {
      title: 'בחינת לשכת עורכי הדין',
      subtitle: 'הכנה מקיפה לבחינת הלשכה',
      icon: '⚖️',
      gradient: 'from-amber-600 to-orange-700',
      examId: 'bar-exam',
      simulatorView: 'bar-simulator',
      simulatorTitle: 'סימולציה מלאה - 120 שאלות (180 דק\')',
      stats: { easy: 600, medium: 600, hard: 600 },
      bilingual: false
    }
  };

  const config = examConfigs[examType] || examConfigs['nursing'];

  useEffect(() => {
    loadChapters();
  }, [examType]);

  const loadChapters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exam-chapters/${config.examId}`);
      if (response.data && response.data.chapters) {
        setChapters(response.data.chapters);
      }
    } catch (err) {
      console.error('Failed to load chapters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChapterQuiz = (chapterId, difficulty = 'mixed') => {
    if (onStartChapterQuiz) {
      onStartChapterQuiz(config.examId, chapterId, difficulty, selectedLanguage);
    } else {
      onStartQuiz('chapter-exam', difficulty, config.examId, chapterId);
    }
  };

  // Chapter colors for visual variety
  const chapterColors = [
    'from-blue-500 to-blue-600',
    'from-emerald-500 to-emerald-600',
    'from-violet-500 to-violet-600',
    'from-amber-500 to-amber-600',
    'from-rose-500 to-rose-600',
    'from-cyan-500 to-cyan-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
    'from-orange-500 to-orange-600',
    'from-pink-500 to-pink-600',
  ];

  if (showChapters) {
    return (
      <div className="space-y-6 animate-slide-in-right">
        {/* Back to exam overview */}
        <button 
          onClick={() => setShowChapters(false)}
          className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
          data-testid="back-from-chapters"
        >
          <ArrowLeft className="w-5 h-5" />
          חזרה לעמוד המבחן
        </button>

        {/* Chapter Header */}
        <div className={`bg-gradient-to-br ${config.gradient} p-6 rounded-3xl text-white`}>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-black">תרגול לפי פרקים</h2>
              <p className="text-white/80">{config.title} - {chapters.length} פרקים</p>
            </div>
          </div>
        </div>

        {/* Language selector for bilingual exams */}
        {config.bilingual && (
          <div className="bg-white p-4 rounded-2xl shadow border flex items-center justify-center gap-4 flex-wrap">
            <Globe className="w-5 h-5 text-slate-500" />
            <span className="font-bold text-slate-700">שפת השאלות:</span>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'all', label: '🌐 הכל' },
                { id: 'he', label: '🇮🇱 עברית' },
                { id: 'en', label: '🇺🇸 English' },
              ].map(lang => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`px-4 py-2 rounded-xl font-bold transition-all ${
                    selectedLanguage === lang.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`lang-${lang.id}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chapter Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {chapters.map((chapter, i) => (
            <div 
              key={chapter.id}
              className="bg-white rounded-2xl shadow-lg border overflow-hidden hover:shadow-xl transition-all"
              data-testid={`chapter-${chapter.id}`}
            >
              <div className={`bg-gradient-to-r ${chapterColors[i % chapterColors.length]} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-black text-lg">{chapter.name}</h3>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold" title="סך השאלות במאגר. בכל תרגול תקבל 30 אקראיות">
                    {chapter.question_count >= 1000
                      ? `${Math.floor(chapter.question_count / 100) / 10}K+ במאגר`
                      : `${chapter.question_count} במאגר`}
                  </span>
                </div>
                {chapter.name_en && (
                  <p className="text-white/70 text-sm mt-1">{chapter.name_en}</p>
                )}
                <p className="text-white/80 text-xs mt-1">🎯 30 שאלות אקראיות בכל תרגול</p>
              </div>
              
              {/* Topics */}
              <div className="p-4">
                <div className="flex flex-wrap gap-1 mb-4">
                  {(chapter.topics || []).map((topic, j) => (
                    <span key={j} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-medium">
                      {topic}
                    </span>
                  ))}
                </div>
                
                {/* Difficulty buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleChapterQuiz(chapter.id, 'easy')}
                    className="bg-green-50 hover:bg-green-100 text-green-700 py-2 rounded-xl text-sm font-bold transition-all"
                    data-testid={`chapter-${chapter.id}-easy`}
                  >
                    קל
                  </button>
                  <button
                    onClick={() => handleChapterQuiz(chapter.id, 'medium')}
                    className="bg-amber-50 hover:bg-amber-100 text-amber-700 py-2 rounded-xl text-sm font-bold transition-all"
                    data-testid={`chapter-${chapter.id}-medium`}
                  >
                    בינוני
                  </button>
                  <button
                    onClick={() => handleChapterQuiz(chapter.id, 'hard')}
                    className="bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded-xl text-sm font-bold transition-all"
                    data-testid={`chapter-${chapter.id}-hard`}
                  >
                    קשה
                  </button>
                </div>
                <button
                  onClick={() => handleChapterQuiz(chapter.id, 'mixed')}
                  className="w-full mt-2 bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-xl text-sm font-bold transition-all"
                  data-testid={`chapter-${chapter.id}-mixed`}
                >
                  תרגול משולב
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Comprehensive exam button */}
        <div className="bg-white p-6 rounded-2xl shadow-lg border text-center">
          <button
            onClick={() => onStartQuiz('gov-exam', 'mixed', config.examId)}
            className={`bg-gradient-to-r ${config.gradient} text-white px-10 py-4 rounded-2xl font-black text-lg shadow-lg hover:shadow-xl transition-all active:scale-95`}
            data-testid="comprehensive-exam"
          >
            <Layers className="w-5 h-5 inline-block ml-2" />
            מבחן מקיף - כל הפרקים
          </button>
          <p className="text-slate-500 text-sm mt-2">שאלות מכל הפרקים ברמות קושי שונות</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in-right">
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
        data-testid={`back-from-${examType}-gov-exam`}
      >
        <ChevronLeft className="w-5 h-5" />
        חזרה
      </button>

      {/* Exam Header */}
      <div className={`bg-gradient-to-br ${config.gradient} p-8 rounded-3xl text-white`}>
        <div className="flex items-center gap-4 mb-4">
          <span className="text-5xl">{config.icon}</span>
          <div>
            <h2 className="text-3xl font-black">{config.title}</h2>
            <p className="text-white/80">{config.subtitle}</p>
          </div>
        </div>
        
        {/* Language selector for bilingual exams */}
        {config.bilingual && (
          <div className="flex items-center gap-3 mt-4 bg-white/10 p-3 rounded-2xl">
            <Globe className="w-5 h-5" />
            <span className="font-bold text-sm">שפה:</span>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'all', label: '🌐 הכל' },
                { id: 'he', label: '🇮🇱 עברית' },
                { id: 'en', label: '🇺🇸 English' },
              ].map(lang => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`px-3 py-1 rounded-lg text-sm font-bold transition-all ${
                    selectedLanguage === lang.id
                      ? 'bg-white text-slate-900'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`main-lang-${lang.id}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Practice by Chapter - NEW */}
      <div className="bg-white p-6 rounded-3xl shadow-lg border">
        <button
          onClick={() => setShowChapters(true)}
          className="w-full bg-gradient-to-r from-slate-800 to-slate-900 text-white p-5 rounded-2xl font-black text-xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-3"
          data-testid="practice-by-chapter"
        >
          <BookOpen className="w-6 h-6" />
          תרגול לפי פרקים
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            {chapters.length > 0 ? `${chapters.length} פרקים` : 'טוען...'}
          </span>
        </button>
        <p className="text-slate-500 text-sm mt-2 text-center">תרגל כל פרק בנפרד לפי מבנה המבחן האמיתי</p>
      </div>

      {/* Difficulty Selection */}
      <div className="bg-white p-8 rounded-3xl shadow-lg border">
        <h3 className="text-2xl font-black text-slate-900 mb-6 text-center">מבחן מקיף - בחר רמת קושי</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => onStartQuiz('gov-exam', 'easy', config.examId)}
            className="group bg-gradient-to-br from-green-400 to-emerald-500 p-6 rounded-2xl text-white text-center shadow-lg hover:shadow-xl transition-all active:scale-95"
            data-testid={`${examType}-gov-exam-easy`}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">😊</div>
            <div className="font-black text-xl">רמה קלה</div>
            <div className="text-sm opacity-80 mt-1">להתחלה ולימוד</div>
          </button>

          <button
            onClick={() => onStartQuiz('gov-exam', 'medium', config.examId)}
            className="group bg-gradient-to-br from-amber-400 to-orange-500 p-6 rounded-2xl text-white text-center shadow-lg hover:shadow-xl transition-all active:scale-95"
            data-testid={`${examType}-gov-exam-medium`}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">💪</div>
            <div className="font-black text-xl">רמה בינונית</div>
            <div className="text-sm opacity-80 mt-1">לחיזוק הידע</div>
          </button>

          <button
            onClick={() => onStartQuiz('gov-exam', 'hard', config.examId)}
            className="group bg-gradient-to-br from-red-500 to-rose-600 p-6 rounded-2xl text-white text-center shadow-lg hover:shadow-xl transition-all active:scale-95"
            data-testid={`${examType}-gov-exam-hard`}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">🔥</div>
            <div className="font-black text-xl">רמה קשה</div>
            <div className="text-sm opacity-80 mt-1">רמת הבחינה האמיתית</div>
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => onStartQuiz('gov-exam', 'mixed', config.examId)}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-8 py-4 rounded-2xl font-black text-lg shadow-lg hover:shadow-xl transition-all active:scale-95"
            data-testid={`${examType}-gov-exam-mixed`}
          >
            מבחן משולב (כל הרמות)
          </button>
        </div>

        {/* Full Simulator Button */}
        <div className="mt-6 text-center border-t pt-6">
          <button
            onClick={() => onStartSimulator(config.simulatorView)}
            className={`bg-gradient-to-r ${config.gradient} text-white px-10 py-5 rounded-2xl font-black text-xl shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center gap-3 mx-auto`}
            data-testid={`${examType}-full-simulator`}
          >
            <Clock className="w-6 h-6" />
            {config.icon} {config.simulatorTitle}
          </button>
          <p className="text-slate-500 text-sm mt-2">מבחן עם טיימר כמו במבחן האמיתי</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 p-4 rounded-xl text-center">
          <div className="text-2xl font-black text-green-600">{config.stats.easy.toLocaleString()}</div>
          <div className="text-xs text-green-700 font-bold">שאלות קלות</div>
        </div>
        <div className="bg-amber-50 p-4 rounded-xl text-center">
          <div className="text-2xl font-black text-amber-600">{config.stats.medium.toLocaleString()}</div>
          <div className="text-xs text-amber-700 font-bold">שאלות בינוניות</div>
        </div>
        <div className="bg-red-50 p-4 rounded-xl text-center">
          <div className="text-2xl font-black text-red-600">{config.stats.hard.toLocaleString()}</div>
          <div className="text-xs text-red-700 font-bold">שאלות קשות</div>
        </div>
      </div>
    </div>
  );
}

export default GovExamView;
